import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PaperPlaneTilt, Bell, EnvelopeSimple, ChatCircle, ArrowLeft, CheckCircle, Spinner
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
  { value: 'all', label: 'Alle Schüler' },
  { value: 'A1', label: 'Nur A1' },
  { value: 'A2', label: 'Nur A2' },
  { value: 'B1', label: 'Nur B1' },
  { value: 'B2', label: 'Nur B2' },
  { value: 'C1', label: 'Nur C1' },
];

export default function TeacherBroadcastPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/dashboard');
  const [target, setTarget] = useState('all');
  const [channels, setChannels] = useState({ inapp: true, push: true, email: true });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/teacher/broadcasts?limit=10`, { withCredentials: true });
      setHistory(res.data.items || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadHistory(); }, []);

  const toggleChannel = (k) => setChannels((c) => ({ ...c, [k]: !c[k] }));

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Titel und Nachricht sind erforderlich');
      return;
    }
    if (!channels.inapp && !channels.push && !channels.email) {
      toast.error('Wähle mindestens einen Kanal');
      return;
    }
    const recipientsLabel = target === 'all' ? 'ALLE Schüler' : `alle ${target}-Schüler`;
    if (!window.confirm(`Diese Nachricht wird an ${recipientsLabel} gesendet. Fortfahren?`)) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/teacher/broadcast`,
        { title: title.trim(), body: body.trim(), url: url.trim() || '/dashboard', target, channels },
        { withCredentials: true }
      );
      setLastResult(res.data);
      toast.success(`Gesendet an ${res.data.recipients} Schüler`);
      setTitle('');
      setBody('');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="teacher-broadcast-page">
      <div className="flex items-center gap-3">
        <Link to="/teacher" className="p-2 rounded-lg text-stone-600 hover:bg-stone-100" data-testid="back-to-teacher">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">Nachrichten an Schüler</h1>
          <p className="text-sm text-[#78716C]">Sende eine Mitteilung per Push, E-Mail und In-App-Benachrichtigung.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#D6D3D1] p-5 sm:p-6 space-y-5" data-testid="broadcast-form">
          <div>
            <Label htmlFor="bc-title">Titel</Label>
            <Input
              id="bc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Morgen Extra-Klasse um 20:00"
              maxLength={120}
              className="mt-1"
              data-testid="broadcast-title-input"
            />
            <p className="text-xs text-[#A8A29E] mt-1">{title.length}/120</p>
          </div>

          <div>
            <Label htmlFor="bc-body">Nachricht</Label>
            <Textarea
              id="bc-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Kurze Nachricht für die Schüler..."
              rows={5}
              maxLength={500}
              className="mt-1"
              data-testid="broadcast-body-input"
            />
            <p className="text-xs text-[#A8A29E] mt-1">{body.length}/500</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bc-target">Empfänger</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1" data-testid="broadcast-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map(l => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bc-url">Link (beim Klick öffnen)</Label>
              <Input
                id="bc-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/dashboard"
                className="mt-1"
                data-testid="broadcast-url-input"
              />
            </div>
          </div>

          <div>
            <Label>Kanäle</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              {[
                { k: 'inapp', label: 'In-App', icon: ChatCircle },
                { k: 'push', label: 'Push', icon: Bell },
                { k: 'email', label: 'E-Mail', icon: EnvelopeSimple },
              ].map(({ k, label, icon: Icon }) => (
                <label
                  key={k}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    channels[k] ? 'border-[#AB1C24] bg-[#FDF0ED]' : 'border-stone-200 hover:border-stone-300'
                  }`}
                  data-testid={`broadcast-channel-${k}`}
                >
                  <Checkbox checked={channels[k]} onCheckedChange={() => toggleChannel(k)} />
                  <Icon size={18} weight={channels[k] ? 'fill' : 'regular'} className={channels[k] ? 'text-[#AB1C24]' : 'text-[#78716C]'} />
                  <span className="text-sm font-medium">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <Button
            onClick={send}
            disabled={sending || !title.trim() || !body.trim()}
            className="w-full bg-[#AB1C24] hover:bg-[#8A151C] text-white gap-2"
            data-testid="broadcast-send-btn"
          >
            {sending ? (
              <><Spinner size={18} className="animate-spin" /> Sendet...</>
            ) : (
              <><PaperPlaneTilt size={18} weight="fill" /> Senden</>
            )}
          </Button>

          {lastResult && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-900 flex items-start gap-2" data-testid="broadcast-last-result">
              <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
              <div>
                Erfolgreich gesendet an <strong>{lastResult.recipients}</strong> Schüler
                <span className="block text-xs text-green-800/80 mt-1">
                  In-App: {lastResult.inapp} · Push: {lastResult.push} · E-Mail: {lastResult.email}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Preview + History */}
        <div className="space-y-5">
          <div className="bg-[#0F1115] text-white rounded-2xl p-5" data-testid="broadcast-preview">
            <p className="text-xs uppercase tracking-wider text-white/50 mb-3">Vorschau</p>
            <div className="bg-white/10 border border-white/10 rounded-xl p-3">
              <div className="flex items-start gap-3">
                <img src="/brand/hca-mark-128.png" alt="" className="w-10 h-10 rounded-lg object-contain bg-white/10" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-white/60">Habla · jetzt</p>
                  <p className="font-semibold text-sm truncate">{title || 'Dein Titel hier'}</p>
                  <p className="text-xs text-white/80 mt-0.5 line-clamp-3">{body || 'Deine Nachricht erscheint hier.'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5" data-testid="broadcast-history">
            <h3 className="font-heading font-semibold text-sm text-[#292524] mb-3">Verlauf</h3>
            {history.length === 0 ? (
              <p className="text-xs text-[#78716C]">Keine Nachrichten gesendet.</p>
            ) : (
              <ul className="space-y-3">
                {history.map(h => (
                  <li key={h.broadcast_id} className="border-l-2 border-[#AB1C24] pl-3" data-testid={`broadcast-item-${h.broadcast_id}`}>
                    <p className="text-sm font-semibold text-[#292524] truncate">{h.title}</p>
                    <p className="text-xs text-[#78716C] line-clamp-2">{h.body}</p>
                    <p className="text-[11px] text-[#A8A29E] mt-1">
                      {new Date(h.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                      {' · '}
                      {h.recipients} Empf · P {h.push_delivered} · M {h.email_delivered}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
